import { Pipe, PipeTransform } from "@angular/core";

@Pipe({
  name: "timeAgo",
  pure: false,
  standalone: true,
})
export class TimeAgoPipe implements PipeTransform {
  transform(value: string | number | Date): string {
    if (!value) return "";

    const now = new Date();
    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return $localize`:@@time-ago.invalid:Invalid date`;
    }

    const diffInMs = now.getTime() - date.getTime();
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    
    // Calculate actual calendar days difference
    const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const inputDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffInDays = Math.floor((nowDate.getTime() - inputDate.getTime()) / (1000 * 60 * 60 * 24));
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInMs < 0) {
      return this.getFutureTimeString(Math.abs(diffInMs));
    }

    if (diffInSeconds < 60) {
      return diffInSeconds <= 5
        ? $localize`:@@time-ago.just-now:just now`
        : $localize`:@@time-ago.seconds-ago:${diffInSeconds} seconds ago`;
    }

    if (diffInMinutes < 60) {
      return diffInMinutes === 1
        ? $localize`:@@time-ago.minute-ago:1 minute ago`
        : $localize`:@@time-ago.minutes-ago:${diffInMinutes} minutes ago`;
    }

    if (diffInHours < 24) {
      return diffInHours === 1
        ? $localize`:@@time-ago.hour-ago:1 hour ago`
        : $localize`:@@time-ago.hours-ago:${diffInHours} hours ago`;
    }

    if (diffInDays === 1) {
      return $localize`:@@time-ago.yesterday:yesterday`;
    }

    if (diffInDays < 7) {
      return $localize`:@@time-ago.days-ago:${diffInDays} days ago`;
    }

    if (diffInWeeks < 4) {
      return diffInWeeks === 1
        ? $localize`:@@time-ago.week-ago:1 week ago`
        : $localize`:@@time-ago.weeks-ago:${diffInWeeks} weeks ago`;
    }

    if (diffInMonths < 12) {
      return diffInMonths === 1
        ? $localize`:@@time-ago.month-ago:1 month ago`
        : $localize`:@@time-ago.months-ago:${diffInMonths} months ago`;
    }

    return diffInYears === 1
      ? $localize`:@@time-ago.year-ago:1 year ago`
      : $localize`:@@time-ago.years-ago:${diffInYears} years ago`;
  }

  private getFutureTimeString(diffInMs: number): string {
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    
    // For future dates, calculate calendar days from the original date objects
    const now = new Date();
    const futureDate = new Date(now.getTime() + diffInMs);
    const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const futureCalendarDate = new Date(futureDate.getFullYear(), futureDate.getMonth(), futureDate.getDate());
    const diffInDays = Math.floor((futureCalendarDate.getTime() - nowDate.getTime()) / (1000 * 60 * 60 * 24));
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInSeconds < 60) {
      return diffInSeconds <= 5
        ? $localize`:@@time-ago.in-moment:in a moment`
        : $localize`:@@time-ago.in-seconds:in ${diffInSeconds} seconds`;
    }

    if (diffInMinutes < 60) {
      return diffInMinutes === 1
        ? $localize`:@@time-ago.in-minute:in 1 minute`
        : $localize`:@@time-ago.in-minutes:in ${diffInMinutes} minutes`;
    }

    if (diffInHours < 24) {
      return diffInHours === 1
        ? $localize`:@@time-ago.in-hour:in 1 hour`
        : $localize`:@@time-ago.in-hours:in ${diffInHours} hours`;
    }

    if (diffInDays === 1) {
      return $localize`:@@time-ago.tomorrow:tomorrow`;
    }

    if (diffInDays < 7) {
      return $localize`:@@time-ago.in-days:in ${diffInDays} days`;
    }

    if (diffInWeeks < 4) {
      return diffInWeeks === 1
        ? $localize`:@@time-ago.in-week:in 1 week`
        : $localize`:@@time-ago.in-weeks:in ${diffInWeeks} weeks`;
    }

    if (diffInMonths < 12) {
      return diffInMonths === 1
        ? $localize`:@@time-ago.in-month:in 1 month`
        : $localize`:@@time-ago.in-months:in ${diffInMonths} months`;
    }

    return diffInYears === 1
      ? $localize`:@@time-ago.in-year:in 1 year`
      : $localize`:@@time-ago.in-years:in ${diffInYears} years`;
  }
}
